import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import { classNames, uniqueUUID } from "../../utils";
import { PersonFormStateError } from "../PersonFormErrorState";
import PersonFormStringDate from "../PersonFormStringDate";
import XIcon from "../../shared-components/icons/XIcon";

export interface JobDefinitions {
  title: string;
  company: string;
  startDate: string;
  endDate: string;
  key: string;
  country: string;
}

type Props = {
  onChange?: (aliases: JobDefinitions[]) => void;
  error?: PersonFormStateError;
  prefill?: JobDefinitions[];
  alternateColor?: boolean;
};

function buildEmptyDefinition(): JobDefinitions {
  return {
    title: "",
    company: "",
    startDate: "",
    endDate: "",
    country: "",
    key: uniqueUUID(),
  };
}

const PersonFormJob = ({ onChange, error, prefill, alternateColor }: Props) => {
  const [fieldsList, setFields] = useState<JobDefinitions[]>([]);
  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "title" | "company" | "startDate" | "endDate" | "country",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: JobDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length >= 1) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <div className="flex">
      {fieldsList.length > 0 && (
        <div className="flex-1 mr-4">
          {fieldsList.map((fields, idx) => (
            <div key={fields.key} className="flex flex-wrap items-center">
              <InputField
                handleChange={(e) => {
                  updateField(idx, "title", e.target.value);
                }}
                name={`title-${idx}`}
                value={fieldsList[idx].title}
                label={"Job Title"}
                classes="flex-1 mr-4"
                className={classNames(
                  error?.jobs?.employer?.[idx] ? "border-rose-600" : "",
                  "w-full"
                )}
              />

              <InputField
                handleChange={(e) => {
                  updateField(idx, "company", e.target.value);
                }}
                name={`company-${idx}`}
                placeholder=""
                value={fieldsList[idx].company}
                label={"Company Name"}
                classes={"flex-1 mr-4"}
                className={classNames(
                  error?.jobs?.employer?.[idx] ? "border-rose-600" : "",
                  "w-full"
                )}
              />
              <PersonFormStringDate
                index={idx}
                error={error}
                _name={`start-date-{idx}`}
                _label="Start Date"
                classes="mb-0"
                prefill={fieldsList[idx].startDate}
                onChange={(e) => {
                  updateField(idx, "startDate", e);
                }}
              ></PersonFormStringDate>
              <PersonFormStringDate
                error={error}
                _name={`end-date-{idx}`}
                _label="End Date"
                index={idx}
                prefill={fieldsList[idx].endDate}
                onChange={(e) => {
                  updateField(idx, "endDate", e);
                }}
              ></PersonFormStringDate>
              <InputField
                handleChange={(e) => {
                  updateField(idx, "country", e.target.value);
                }}
                name={`country-${idx}`}
                placeholder=""
                label={"Country"}
                value={fieldsList[idx].country}
                classes={"mr-4"}
              />
              {idx >= 0 && (
                <span className="pl-2 h-0">
                  <div
                    className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                    onClick={(e) => handleRemove(e, idx)}
                  >
                    <XIcon />
                  </div>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="self-end w-44">
        <Button
          type={ButtonTypes.add}
          text="Add Job"
          colorType={
            alternateColor
              ? ButtonColorTypes.alternate
              : ButtonColorTypes.action
          }
          className="w-44"
          onClick={handleAdd}
        />
      </div>
    </div>
  );
};

export default PersonFormJob;
